/**
 * The two facts a station computer reports about itself that are read from
 * IndexedDB rather than derived from readiness (§26).
 *
 * Their own file rather than lines inside `entrypoints/device.ts`, because that
 * file is an entrypoint — importing it boots the agent — and these are the half
 * of the heartbeat with an answer worth being wrong about. Both were declared on
 * `HeartbeatReport` and permitted by the server from the beginning and neither
 * was ever populated, so `station_devices.pending_mutation_count` was null on
 * every row and the administrator's "changes held" figure read zero for a fleet
 * that might be holding a shift's work.
 */
import { offlineDatabase, type OfflineDatabase } from "../db/schema"
import { pendingMutations } from "../outbox/outbox"
import type { HeartbeatReport } from "./heartbeat"

/**
 * What this computer is still holding, and how long it has held it.
 *
 * Read from the outbox on every heartbeat rather than tracked as a counter, for
 * the same reason readiness is re-derived rather than cached: a counter records
 * that something was queued, not that it is still there.
 *
 * `pendingMutations` is sorted by `deviceSeq`, which is monotonic per device, so
 * the first row is the earliest queued — but the *time* to report is
 * `occurredAt`, and the two disagree if the machine's clock stepped between two
 * writes. The minimum is taken so that "oldest" means the oldest moment, which
 * is the sense an administrator reads it in.
 */
export async function outboxReport(db: OfflineDatabase = offlineDatabase()): Promise<HeartbeatReport> {
  const pending = await pendingMutations(db)
  // Zero rather than omitted. Absent and zero are different answers on the
  // server — see `reported_attributes` — and a computer that has drained must
  // say so, or its last non-zero count stands for ever.
  if (pending.length === 0) return { pendingMutationCount: 0 }

  const oldest = pending.reduce((earliest, mutation) =>
    mutation.occurredAt < earliest.occurredAt ? mutation : earliest
  )

  return { pendingMutationCount: pending.length, oldestPendingOccurredAt: oldest.occurredAt }
}

/**
 * Which authorization snapshot this computer is enforcing.
 *
 * The bootstrap reports it once, at the moment it lands. Without it here a
 * device that never re-bootstraps never mentions it again, and a machine still
 * enforcing a superseded access matrix stays invisible for exactly as long as it
 * keeps working.
 */
export async function snapshotReport(db: OfflineDatabase = offlineDatabase()): Promise<HeartbeatReport> {
  const stored = await db.snapshot.toCollection().first()

  return stored ? { authorizationSnapshotVersion: stored.version } : {}
}
